import React, { useState } from "react";
import CustomInput from "../../common/CustomInput";
import { ReactSVG } from "react-svg";
import Button from "../../common/Button";
import {
  countryOptions,
  designationOptions,
  physicianOptions,
  stateOptions,
} from "./options";
import Collpsable from "../../common/Collpsable";
import {
  isFieldError,
  isValidPhoneNumber,
  isFieldWithLengthError,
} from "../../../helpers/input-validations";
import { useDispatch } from "react-redux";
import { createPatient } from "../../../redux/modules/patient/actions";
import {
  calculateAge,
  extractPhoneNumber,
  formatToYYYYMMDDBackend,
  scrollY,
} from "../../../helpers/functions";
import { useNavigate } from "react-router-dom";
import { initialPatientCreateObject } from "../Create";

const FourthStep = ({
  patientCreateObject,
  setPatientCreateObject,
  currentStep,
  setCurrentStep,
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [careProviderNumber, setCareProviderNumber] = useState(
    patientCreateObject?.careProviders?.length || 0
  );
  const [careProviders, setCareProviders] = useState(
    patientCreateObject.careProviders.length > 0
      ? patientCreateObject.careProviders
      : initialPatientCreateObject.careProviders
  );
  const [skipMode, setSkipMode] = useState(false);

  const getRequiredBg = (val) => {
    const isEmpty =
      val == null ||
      (typeof val === "string" && val.trim() === "") ||
      (Array.isArray(val) && val.length === 0);

    return isEmpty ? "!bg-blue-100" : "!bg-white";
  };

  const handleInputChange = (index, key, value) => {
    const updatedCareProviders = [...careProviders];
    updatedCareProviders[index][key] = value;

    const {
      firstNameCareProvider,
      middleNameCareProvider,
      lastNameCareProvider,
      licenseNumberCareProvider,
      designationCareProvider,
      faxCareProvider,
      phoneNumberCareProvider,
    } = updatedCareProviders[index];

    // Perform validation and set error messages
    const errors = {
      firstNameCareProvider: false,
      middleNameCareProvider: false,
      lastNameCareProvider: false,
      licenseNumberCareProvider: false,
      designationCareProvider: false,
      faxCareProvider: false,
      phoneNumberCareProvider: false,
    };

    if (key === "lastNameCareProvider") {
      errors.lastNameCareProvider = isFieldError(lastNameCareProvider);
    } else if (key === "middleNameCareProvider") {
      errors.middleNameCareProvider = isFieldError(middleNameCareProvider);
    } else if (key === "firstNameCareProvider") {
      errors.firstNameCareProvider = isFieldError(firstNameCareProvider);
    } else if (key === "licenseNumberCareProvider") {
      errors.licenseNumberCareProvider = isFieldWithLengthError(
        licenseNumberCareProvider,
        5
      );
    } else if (key === "designationCareProvider") {
      errors.designationCareProvider = isFieldError(designationCareProvider);
    } else if (key === "phoneNumberCareProvider") {
      errors.phoneNumberCareProvider = !isValidPhoneNumber(
        phoneNumberCareProvider
      );
    } else if (key === "faxCareProvider") {
      errors.faxCareProvider = !isValidPhoneNumber(faxCareProvider);
    }

    // Set error state
    updatedCareProviders[index].errors = errors;
    // Update the isCompleted flag
    updatedCareProviders[index].isCompleted =
      firstNameCareProvider?.length > 0 &&
      !errors.firstNameCareProvider &&
      lastNameCareProvider?.length > 0 &&
      !errors.lastNameCareProvider &&
      licenseNumberCareProvider?.length > 0 &&
      !errors.licenseNumberCareProvider &&
      designationCareProvider?.length > 0 &&
      !errors.designationCareProvider &&
      faxCareProvider?.length > 0 &&
      !errors.faxCareProvider &&
      phoneNumberCareProvider?.length > 0 &&
      !errors.phoneNumberCareProvider;
    setCareProviders(updatedCareProviders);
  };
  const addCareProvider = () => {
    let hasValidationError = false; // Flag to track validation errors

    // Check for validation errors in existing care providers
    const updatedCareProviders = careProviders.map((provider) => {
      const {
        firstNameCareProvider,
        middleNameCareProvider,
        lastNameCareProvider,
        licenseNumberCareProvider,
        designationCareProvider,
        phoneNumberCareProvider,
        faxCareProvider,
      } = provider;

      // Validate the required fields for each provider
      const providerErrors = {
        lastNameCareProvider: isFieldError(lastNameCareProvider),
        middleNameCareProvider: isFieldError(middleNameCareProvider),
        firstNameCareProvider: isFieldError(firstNameCareProvider),
        licenseNumberCareProvider: isFieldError(licenseNumberCareProvider),
        designationCareProvider: isFieldError(designationCareProvider),
        phoneNumberCareProvider: !isValidPhoneNumber(phoneNumberCareProvider),
        faxCareProvider: !isValidPhoneNumber(faxCareProvider),
      };

      // If any care provider has incomplete required fields, mark it as invalid
      if (
        providerErrors.lastNameCareProvider ||
        providerErrors.middleNameCareProvider ||
        providerErrors.firstNameCareProvider ||
        providerErrors.licenseNumberCareProvider ||
        providerErrors.designationCareProvider ||
        providerErrors.phoneNumberCareProvider ||
        providerErrors.faxCareProvider
      ) {
        hasValidationError = true;
      }

      return { ...provider, errors: { ...providerErrors }, isEditing: false };
    });

    // If there's a validation error, don't add a new care provider
    if (hasValidationError) {
      setCareProviders(updatedCareProviders);
      return;
    }

    // Add a new empty care provider
    updatedCareProviders.push({
      firstNameCareProvider: "",
      middleNameCareProvider: "",
      lastNameCareProvider: "",
      licenseNumberCareProvider: "",
      designationCareProvider: "",
      isEditing: true,
      isCompleted: false,
      phoneCountryCareProvider: "CA",
      phoneNumberCareProvider: "",
      faxCareProvider: "",
      emailCareProvider: "",
      addressCareProvider: "",
      unitCareProvider: "",
      poBoxCareProvider: "",
      cityCareProvider: "",
      stateCareProvider: "",
      postalCodeCareProvider: "",
      countryCareProvider: "",
      commentsCareProvider: "",
      errors: {
        firstNameCareProvider: "",
        middleNameCareProvider: "",
        lastNameCareProvider: "",
        licenseNumberCareProvider: "",
        designationCareProvider: "",
        phoneNumberCareProvider: "",
        faxCareProvider: "",
      },
    });

    setCareProviders(updatedCareProviders);
  };

  const removeCareProvider = (index) => {
    const updatedCareProviders = [...careProviders];

    if (updatedCareProviders[index].isEditing) {
      // If the plan being removed is in editing mode, cancel editing first
      updatedCareProviders[index].isEditing = false;
    }

    updatedCareProviders.splice(index, 1);
    setCareProviders(updatedCareProviders);
  };

  const editPlan = (index) => {
    const updatedCareProviders = careProviders.map((plan, i) => ({
      ...plan,
      isEditing: i === index,
    }));
    setCareProviders(updatedCareProviders?.filter((plan) => plan.isCompleted));
  };

  const handleSubmit = async () => {
    const finalCareProviders = careProviders.filter(
      (careProvider) => careProvider.isCompleted
    );
    setPatientCreateObject({
      ...patientCreateObject,
      careProviders: finalCareProviders,
    });

    //set blank object if values haven't set and skipped the step.
    const provincialInsurance =
      patientCreateObject.healthCardNumber != "" &&
      patientCreateObject.healthCardVersionCode != ""
        ? {
            cardNumber: extractPhoneNumber(
              patientCreateObject.healthCardNumber
            ),
            expiryDate: formatToYYYYMMDDBackend(
              patientCreateObject.healthCardExpiryDate
            ),
            versionCode: patientCreateObject.healthCardVersionCode,
            province: patientCreateObject.healthCardProvince,
          }
        : null;

    //set blank array if values haven't set and skipped the step.
    const insurancePlans =
      patientCreateObject.insurancePlans[0]?.insurancePlanName != "" &&
      patientCreateObject.insurancePlans[0]?.orderOfInsurance != ""
        ? patientCreateObject.insurancePlans.map((plan) => ({
            insurancePlanName: plan.insurancePlanName,
            orderOfInsurance: plan.orderOfInsurance,
            expiryDate: formatToYYYYMMDDBackend(plan.insuranceExpiryDate),
            effectiveDate: formatToYYYYMMDDBackend(plan.insuranceEffectiveDate),
            comments: plan.comments,
            carrierId: null,
            groupId: null,
            clientId: null,
            CPHACode: null,
            dateOfBirth: formatToYYYYMMDDBackend(
              plan?.insuranceDateOfBirth || patientCreateObject.dateOfBirth
            ),
            relationship: null,
            deductType: null,
            deductValue: null,
            firstName: null,
            lastName: null,
          }))
        : [];
        
    const transformedObject = {
      firstName: patientCreateObject.firstName,
      middleName: patientCreateObject.middleName,
      lastName: patientCreateObject.lastName,
      dateOfBirth: formatToYYYYMMDDBackend(patientCreateObject.dateOfBirth),
      biologicalSex: patientCreateObject.biologicalSex || null,
      gender: patientCreateObject.gender || null,
      race: patientCreateObject.race || null,
      characteristics: patientCreateObject.characteristics || null,
      medicationAdministration: patientCreateObject.medicationAdministration || null,
      comments: patientCreateObject.commentsFirstStep || null,
      payingCash:
        patientCreateObject.provincialInsurance === "noInsuranceConcent"
          ? true
          : false,
      colorBlind: false, // Set to false, adjust as needed
      status: "Active", // Set to desired value
      age: calculateAge(patientCreateObject.dateOfBirth), // Calculate age based on dateOfBirth
      primaryLanguage: patientCreateObject.primaryLanguage,
      contactInfo: {
        noPhone: patientCreateObject.noPhone || false,
        primaryPhoneCountryCode: patientCreateObject.noPhone
          ? ""
          : patientCreateObject.primaryPhoneCountry,
        primaryPhone: patientCreateObject.noPhone
          ? ""
          : extractPhoneNumber(patientCreateObject.phoneNumber) || null,
        secondaryPhoneCountryCode: patientCreateObject.noPhone
          ? ""
          : patientCreateObject.secondaryPhoneCountry,
        secondaryPhone: patientCreateObject.noPhone
          ? ""
          : extractPhoneNumber(patientCreateObject.secondaryPhoneNumber) ||
            null,
        email: patientCreateObject.email,
        noAddress: patientCreateObject.noAddress || false,
        longTermCare: patientCreateObject.longTermCare || false,
        nameOfFacility: patientCreateObject.longTermCare
          ? patientCreateObject.facility || null
          : null,
        address: patientCreateObject.noAddress
          ? null
          : patientCreateObject.address || null,
        city: patientCreateObject.city,
        province: patientCreateObject.state,
        postalCode: patientCreateObject.postalCode
          ? patientCreateObject.postalCode.replace(/\s/g, "")
          : null,
        primaryContact:
          patientCreateObject?.contactOptions === "primary" ? false : true, // Set to true or false as needed
        country: patientCreateObject.country,
        unitNumber: patientCreateObject.noAddress
          ? null
          : patientCreateObject.unit || null,
        poBox: patientCreateObject.noAddress
          ? null
          : patientCreateObject.poBox || null,
        comments: patientCreateObject.commentsFirstStep || null,
      },
      provincialInsurance: provincialInsurance,
      thirdPartyInsurance: insurancePlans,
      caregiver: patientCreateObject?.skipCaregiver
        ? []
        : [
            {
              firstName: patientCreateObject.firstNameCaregiver,
              middleName: patientCreateObject.middleNameCaregiver,
              lastName: patientCreateObject.lastNameCaregiver,
              relationship: patientCreateObject.relationshipTypeCaregiver,
              comments: patientCreateObject.commentsCaregiver || null,
              contactInfo: {
                secondaryPhoneCountryCode:
                  patientCreateObject.phoneCountryCareGiver,
                primaryPhoneCountryCode:
                  patientCreateObject.secondaryPhoneCountryCareGiver,
                primaryPhone:
                  extractPhoneNumber(
                    patientCreateObject.phoneNumberCaregiver
                  ) || null,
                secondaryPhone:
                  extractPhoneNumber(
                    patientCreateObject.secondaryPhoneNumberCaregiver
                  ) || null,
                email: patientCreateObject.emailCaregiver || null,
                address: patientCreateObject.addressCaregiver || null,
                city: patientCreateObject.cityCaregiver || null,
                province: patientCreateObject.stateCaregiver || null,
                postalCode: patientCreateObject.postalCodeCaregiver
                  ? patientCreateObject.postalCodeCaregiver.replace(/\s/g, "")
                  : null,
                primaryContact:
                  patientCreateObject?.contactOptionsCaregiver === "primary"
                    ? true
                    : false, // Set to true or false as needed
                country: patientCreateObject.countryOptionsCaregiver || null,
                poBox: patientCreateObject.poBoxCaregiver || null,
                comments: patientCreateObject.commentsCaregiver || null,
              },
            },
          ],
      careProvider: finalCareProviders.map((provider) => ({
        firstName: provider.firstNameCareProvider,
        middleName: provider.middleNameCareProvider,
        lastName: provider.lastNameCareProvider,
        title: provider.designationCareProvider,
        licenseNumber: provider.licenseNumberCareProvider,
        notes: provider.commentsCareProvider || null,
        primaryProvider:
          provider.contactOptionsCareProvider === "primary" ? true : false, // Set to true or false as needed
        contactInfo: {
          primaryPhoneCountryCode: provider.phoneCountryCareProvider || null,
          primaryPhone:
            extractPhoneNumber(provider.phoneNumberCareProvider) || null,
          email: provider.emailCareProvider || null,
          address: provider.addressCareProvider || null,
          city: provider.cityCareProvider || null,
          province: provider.stateCareProvider || null,
          fax: extractPhoneNumber(provider.faxCareProvider) || null,
          postalCode:
            provider.postalCodeCareProvider.replace(/\s/g, "") || null,
          primaryContact: true, // Set to true or false as needed
          country: provider.countryCareProvider || null,
          poBox: provider.poBoxCareProvider || null,
        },
      })),
    };
    dispatch(createPatient(transformedObject, navigate, onClose));
  };

  const manageBack = () => {
    const completedCareProviders = careProviders.filter(
      (careProvider) => careProvider.isCompleted
    );
    if (completedCareProviders.length > 0) {
      setPatientCreateObject({
        ...patientCreateObject,
        careProviders: completedCareProviders,
      });
      setCurrentStep(currentStep - 1);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  const hasIncompleteEditingProvider = careProviders.some(
    (provider) => provider.isEditing && !provider.isCompleted
  );

  return (
    <>
      <div
        className="flex flex-col px-8 justify-between flex-1 py-6 h-full overflow-y-auto"
        id="form-body"
      >
        <div className="grid grid-cols-12 gap-3">
          <div
            className="flex items-center gap-4 col-span-12 border p-4 rounded-[4px] bg-blue-100 border-blue-200"
          >
            <ReactSVG src="/icons/caution-blue.svg" className="w-10 h-10 " />
            <p className="flex items-center justify-between w-full gap-4 text-gray-700 text-body-sm">
              <span>
                <>
                  <span className="font-semibold">Skip Step:</span>{" "}
                  if there is NO Primary Care Provider.
                </>
              </span>
              <Button
                onClick={() => {
                  setSkipMode(true);
                }}
                className="text-body-md font-semibold py-3.5 px-8 whitespace-nowrap"
                text="Skip Step"
              />
            </p>
          </div>
          {careProviders
            .filter((careProvider) => careProvider.isCompleted)
            .map((careProvider, index) => {
              return (
                <div
                  className="grid grid-cols-12 col-span-12 gap-3"
                  key={index}
                >
                  {careProvider.isCompleted ? (
                    <Collpsable
                      onEdit={() => editPlan(index)}
                      onDelete={() => removeCareProvider(index)}
                      className={
                        "col-span-12 transition-all ease-in-out duration-500"
                      }
                      title={`${careProvider.lastNameCareProvider} ${careProvider.middleNameCareProvider} ${careProvider.firstNameCareProvider}`}
                    />
                  ) : null}
                </div>
              );
            })}
          {careProviders.map((careProvider, index) => {
            return careProvider.isEditing || careProviders.length === 1 ? (
              <div className="grid grid-cols-12 col-span-12 gap-3" key={index}>
                <CustomInput
                  name={`lastNameCareProvider_${index}`}
                  className={`col-span-4 ${getRequiredBg(careProviders[index].lastNameCareProvider)}`}
                  label="Last Name"
                  placeholder="Enter last name"
                  type="text"
                  value={careProviders[index].lastNameCareProvider}
                  capitalizeFirstLetter={true}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "lastNameCareProvider",
                      e.target.value
                    )
                  }
                  error={careProviders[index].errors.lastNameCareProvider}
                  required
                />
                <CustomInput
                  name={`middleNameCareProvider_${index}`}
                  className="col-span-4"
                  label="Middle Name"
                  placeholder="Enter middle name"
                  type="text"
                  value={careProviders[index].middleNameCareProvider}
                  capitalizeFirstLetter={true}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "middleNameCareProvider",
                      e.target.value
                    )
                  }
                  error={careProviders[index].errors.middleNameCareProvider}
                />
                <CustomInput
                  name={`firstNameCareProvider_${index}`}
                  className={`col-span-4 ${getRequiredBg(careProviders[index].firstNameCareProvider)}`}
                  label="First Name"
                  placeholder="Enter first name"
                  type="text"
                  value={careProviders[index].firstNameCareProvider}
                  capitalizeFirstLetter={true}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "firstNameCareProvider",
                      e.target.value
                    )
                  }
                  error={careProviders[index].errors.firstNameCareProvider}
                  required
                />
                <CustomInput
                  name={`licenseNumberCareProvider_${index}`}
                  className={`col-span-6 ${getRequiredBg(careProviders[index].licenseNumberCareProvider)}`}
                  label="License Number"
                  placeholder="XXXXX"
                  type="text"
                  value={careProviders[index].licenseNumberCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "licenseNumberCareProvider",
                      e.target.value
                    )
                  }
                  error={careProviders[index].errors.licenseNumberCareProvider}
                  required
                />
                <CustomInput
                  options={designationOptions}
                  name={`designationCareProvider_${index}`}
                  className={`col-span-6 ${getRequiredBg(careProviders[index].designationCareProvider)}`}
                  label="Designation"
                  type="dropdown"
                  value={careProviders[index].designationCareProvider}
                  onChange={(e) =>
                    handleInputChange(index, "designationCareProvider", e.value)
                  }
                  error={careProviders[index].errors.designationCareProvider}
                  required
                />
                <p className="pl-2 pb-3 col-span-12 text-gray-700 text-body-xs">
                  <span>
                    Search license number{" "}
                    <u>
                      <a target="_blank" href="https://www.cpso.on.ca">
                        here
                      </a>
                    </u>
                    .
                  </span>
                </p>
                <CustomInput
                  className="col-span-12"
                  options={physicianOptions}
                  type="radio"
                  name={`contactOptionsCareProvider_${index}`}
                  value={careProviders[index].contactOptionsCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "contactOptionsCareProvider",
                      e.target.value
                    )
                  }
                />
                <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
                <p className="col-span-12 font-medium text-gray-900 text-body-md">
                  Contact Information
                </p>
                <CustomInput
                  name={`phoneNumberCareProvider_${index}`}
                  className={`col-span-6 ${getRequiredBg(careProviders[index].phoneNumberCareProvider)}`}
                  label="Phone Number"
                  placeholder="(555) 000-0000"
                  type="phone"
                  handleSelect={(e) =>
                    handleInputChange(
                      index,
                      "phoneCountryCareProvider",
                      e.target.value
                    )
                  }
                  error={careProviders[index].errors.phoneNumberCareProvider}
                  value={careProviders[index].phoneNumberCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "phoneNumberCareProvider",
                      e.target.value
                    )
                  }
                  onFocus={(e) => scrollY("form-body")}
                  required
                />
                <CustomInput
                  name={`faxCareProvider_${index}`}
                  className={`col-span-6 ${getRequiredBg(careProviders[index].faxCareProvider)}`}
                  label="FAX"
                  mask="(999) 999-9999"
                  placeholder="Enter Fax"
                  type="text"
                  value={careProviders[index].faxCareProvider}
                  onChange={(e) =>
                    handleInputChange(index, "faxCareProvider", e.target.value)
                  }
                  error={careProviders[index].errors.faxCareProvider}
                  required
                />
                <CustomInput
                  name={`emailCareProvider_${index}`}
                  className="col-span-12"
                  label="Email Address"
                  placeholder="Enter Email Address"
                  type="email"
                  value={careProviders[index].emailCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "emailCareProvider",
                      e.target.value
                    )
                  }
                />
                <CustomInput
                  name={`addressCareProvider_${index}`}
                  className="col-span-12"
                  label="Address"
                  placeholder=" Enter Street Number & Street Name"
                  type="text"
                  value={careProviders[index].addressCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "addressCareProvider",
                      e.target.value
                    )
                  }
                />
                <CustomInput
                  name={`unitCareProvider_${index}`}
                  className="col-span-6"
                  label="Unit Number"
                  placeholder="Enter Unit Number"
                  type="text"
                  value={careProviders[index].unitCareProvider}
                  onChange={(e) =>
                    handleInputChange(index, "unitCareProvider", e.target.value)
                  }
                />
                <CustomInput
                  name={`poBoxCareProvider_${index}`}
                  className="col-span-6"
                  label="PO Box"
                  placeholder="PO Box"
                  type="age"
                  value={careProviders[index].poBoxCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "poBoxCareProvider",
                      e.target.value
                    )
                  }
                />
                <CustomInput
                  name={`cityCareProvider_${index}`}
                  className="col-span-6"
                  label="City"
                  placeholder="Enter City"
                  type="text"
                  value={careProviders[index].cityCareProvider}
                  onChange={(e) =>
                    handleInputChange(index, "cityCareProvider", e.target.value)
                  }
                />
                <CustomInput
                  options={stateOptions}
                  name={`stateCareProvider_${index}`}
                  className="col-span-6"
                  label="Province/State"
                  type="dropdown"
                  value={careProviders[index].stateCareProvider}
                  onChange={(e) =>
                    handleInputChange(index, "stateCareProvider", e.value)
                  }
                />
                <CustomInput
                  name={`postalCodeCareProvider_${index}`}
                  className="col-span-6"
                  label="Postal Code"
                  placeholder="Enter Postal Code"
                  type="postalCode"
                  value={careProviders[index].postalCodeCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "postalCodeCareProvider",
                      e.target.value
                    )
                  }
                />
                <CustomInput
                  options={countryOptions}
                  name={`countryCareProvider_${index}`}
                  className="col-span-6"
                  label="Country"
                  type="dropdown"
                  value={careProviders[index].countryCareProvider}
                  onChange={(e) =>
                    handleInputChange(index, "countryCareProvider", e.value)
                  }
                />
                <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
                <CustomInput
                  name={`commentsCareProvider_${index}`}
                  className="col-span-12"
                  label="Comments"
                  placeholder="Enter Note"
                  type="text"
                  value={careProviders[index].commentsCareProvider}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      "commentsCareProvider",
                      e.target.value
                    )
                  }
                />
              </div>
            ) : null;
          })}
        </div>
      </div>
      <div className="flex items-center justify-center py-6">
          <div className="flex items-center justify-center space-x-5">
          {skipMode ? (
            <>
              <Button
                className="min-w-[200px]"
                text={"Cancel"}
                type="secondary"
                onClick={() => setSkipMode(false)}
              />
              <Button
                className="min-w-[200px]"
                text={"Back"}
                type="secondary"
                onClick={() => {
                  setSkipMode(false);
                  manageBack();
                }}
              />
              <Button
                className="min-w-[200px]"
                text={"Complete & Open Chart"}
                type="primary"
                onClick={() => handleSubmit()}
              />
            </>
          ) : (
            <>
              <Button
                className="min-w-[200px]"
                text={currentStep === 1 ? `Cancel` : "Go Back"}
                type="secondary"
                onClick={() => (currentStep === 1 ? onClose(false) : manageBack())}
              />
              <Button
                className="min-w-[200px]"
                text={"Save & Add Another"}
                type="primary"
                disabled={hasIncompleteEditingProvider}
                onClick={() => addCareProvider(careProviderNumber)}
              />
              <Button
                className="min-w-[200px]"
                text="Complete & Open Chart"
                type="primary"
                disabled={hasIncompleteEditingProvider}
                onClick={() => handleSubmit()}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FourthStep;
